import React, { useState, useCallback, useMemo } from 'react';
import { Form, Table, Input, Select, Checkbox, Button, Space, Tooltip, Dropdown, theme } from 'antd';
import { PlusOutlined, DeleteOutlined, CopyOutlined, SettingOutlined, ReloadOutlined } from '@ant-design/icons';
import { useAntdResizableHeader } from 'use-antd-resizable-header';
import 'use-antd-resizable-header/dist/style.css';
import type { DynamicGridConfig, ValueChangeEvent, GridColumn, GridRow, DropdownOption } from '../types';
import { getDynamicGridOptions } from '../types';
import { telemetryService } from '../../../api';
import { createContextLogger } from '../../../utils/logger';

const logger = createContextLogger('DynamicGrid');

interface DynamicGridProps {
  config: DynamicGridConfig;
  value?: GridRow[];
  onChange: (event: ValueChangeEvent) => void;
  pageId: string;
  error?: string[];
  // Access to all form values to monitor source grid changes
  allFormValues: Record<string, any>;
}

export const DynamicGrid: React.FC<DynamicGridProps> = ({
  config,
  value = [],
  onChange,
  pageId,
  error,
  allFormValues
}) => {
  const { token } = theme.useToken();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  
  // Extract dynamic grid options
  const dynamicGridOptions = getDynamicGridOptions(config);
  
  // Debug: Track allFormValues changes
  React.useEffect(() => {
    logger.debug('DynamicGridTest 🔄 allFormValues changed:', {
      gridId: config.id,
      sourceGridId: dynamicGridOptions.sourceGridId,
      allFormValuesKeys: Object.keys(allFormValues),
      sourceGridData: allFormValues[dynamicGridOptions.sourceGridId],
      sourceGridDataLength: allFormValues[dynamicGridOptions.sourceGridId]?.length || 0
    });
  }, [allFormValues, config.id, dynamicGridOptions.sourceGridId]);
  
  // Generate columns based on source grid data
  const generatedColumns = useMemo(() => {
    logger.debug('DynamicGridTest 🔄 generatedColumns useMemo triggered:', {
      gridId: config.id,
      sourceGridId: dynamicGridOptions.sourceGridId,
      allFormValuesKeys: Object.keys(allFormValues)
    });
    
    const sourceGridData = allFormValues[dynamicGridOptions.sourceGridId];
    const staticColumns = dynamicGridOptions.staticColumns || [];
    const columnNameField = dynamicGridOptions.columnNameField || 'name';
    const columnTypeField = dynamicGridOptions.columnTypeField || 'type';
    
    logger.debug('DynamicGridTest 📋 Source grid data check:', {
      sourceGridData,
      isArray: Array.isArray(sourceGridData),
      length: sourceGridData?.length
    });
    
    if (!sourceGridData || !Array.isArray(sourceGridData)) {
      logger.debug('DynamicGridTest ❌ No source grid data, returning static columns only');
      logger.debug('DynamicGrid: No source grid data available for dynamic columns', {
        sourceGridId: dynamicGridOptions.sourceGridId,
        sourceGridData,
        allFormValues: Object.keys(allFormValues)
      });
      return staticColumns;
    }
    
    // Debug: Log the source grid data processing
    logger.debug('DynamicGrid: Processing source grid data', {
      sourceGridId: dynamicGridOptions.sourceGridId,
      columnNameField,
      columnTypeField,
      sourceGridData,
      rowCount: sourceGridData.length
    });
    
    // Generate columns from source grid data
    const dynamicColumns: GridColumn[] = sourceGridData.map((row: any, index: number) => {
      const columnName = row[columnNameField] || `field_${index}`;
      const columnType = row[columnTypeField] || 'text';
      
      // Debug: Log each column generation
      logger.debug('DynamicGrid: Generating column', {
        index,
        row,
        columnName,
        columnType,
        columnNameField,
        columnTypeField
      });
      
      // Map Hop data types to UI column types
      let uiColumnType: GridColumn['type'] = 'text';
      if (columnType.toLowerCase().includes('boolean')) {
        uiColumnType = 'checkbox';
      } else if (columnType.toLowerCase().includes('number') || 
                 columnType.toLowerCase().includes('integer') ||
                 columnType.toLowerCase().includes('bigint')) {
        uiColumnType = 'number';
      }
      
      return {
        id: columnName,
        label: columnName,
        type: uiColumnType,
        width: 150,
        placeholder: `Enter ${columnName}`,
        required: false,
        resizable: true
      };
    });
    
    const finalColumns = [...staticColumns, ...dynamicColumns];
    logger.debug('DynamicGridTest ✅ Generated columns:', {
      staticColumnsCount: staticColumns.length,
      dynamicColumnsCount: dynamicColumns.length,
      totalColumnsCount: finalColumns.length,
      columnNames: finalColumns.map(col => col.id)
    });
    
    return finalColumns;
  }, [allFormValues, dynamicGridOptions.sourceGridId, dynamicGridOptions.columnNameField, dynamicGridOptions.columnTypeField, dynamicGridOptions.staticColumns]);
  
  // Helper function to generate default row data based on dynamic columns
  const generateDefaultRowData = useCallback(() => {
    const defaultData: any = { id: `row_${Date.now()}` };
    
    // Apply column default values
    generatedColumns.forEach(column => {
      if (column.defaultValue !== undefined) {
        defaultData[column.id] = column.defaultValue;
      } else if (column.type === 'checkbox') {
        defaultData[column.id] = false;
      } else {
        defaultData[column.id] = '';
      }
    });
    
    return defaultData;
  }, [generatedColumns]);

  const handleCellChange = useCallback((rowIndex: number, columnId: string, newValue: any) => {
    const newData = [...value];
    
    // Ensure row exists
    if (!newData[rowIndex]) {
      newData[rowIndex] = generateDefaultRowData();
      newData[rowIndex].id = `row_${rowIndex}`;
    }
    
    newData[rowIndex][columnId] = newValue;

    onChange({
      componentId: config.id,
      value: newData,
      pageId
    });

    // Track telemetry for grid changes
    telemetryService.trackEvent('dynamic_grid_cell_changed', {
      gridId: config.id,
      columnId,
      rowIndex,
      sourceGridId: dynamicGridOptions.sourceGridId
    });
  }, [value, onChange, config.id, pageId, dynamicGridOptions.sourceGridId, generateDefaultRowData]);

  const handleAddRow = useCallback(() => {
    const newRow: GridRow = generateDefaultRowData();
    // Use index-based ID for consistency
    newRow.id = `row_${value.length}`;
    const newData = [...value, newRow];
    
    onChange({
      componentId: config.id,
      value: newData,
      pageId
    });

    // Track telemetry
    telemetryService.trackEvent('dynamic_grid_row_added', {
      gridId: config.id,
      rowCount: newData.length,
      sourceGridId: dynamicGridOptions.sourceGridId
    });
  }, [value, onChange, config.id, pageId, generateDefaultRowData, dynamicGridOptions.sourceGridId]);

  const handleRemoveRows = useCallback(() => {
    if (selectedRowKeys.length === 0) return;
    
    const newData = value.filter((_, index) => !selectedRowKeys.includes(index));
    
    onChange({
      componentId: config.id,
      value: newData,
      pageId
    });
    
    setSelectedRowKeys([]);

    // Track telemetry
    telemetryService.trackEvent('dynamic_grid_rows_removed', {
      gridId: config.id,
      removedCount: selectedRowKeys.length,
      remainingCount: newData.length,
      sourceGridId: dynamicGridOptions.sourceGridId
    });
  }, [selectedRowKeys, value, onChange, config.id, pageId, dynamicGridOptions.sourceGridId]);

  const handleCopyRows = useCallback(() => {
    if (selectedRowKeys.length === 0) return;
    
    const rowsToCopy = selectedRowKeys.map(key => value[key as number]).filter(Boolean);
    const newRows = rowsToCopy.map(row => ({
      ...row,
      id: `row_${Date.now()}_${Math.random()}`
    }));
    
    const newData = [...value, ...newRows];
    
    onChange({
      componentId: config.id,
      value: newData,
      pageId
    });
    
    setSelectedRowKeys([]);

    // Track telemetry
    telemetryService.trackEvent('dynamic_grid_rows_copied', {
      gridId: config.id,
      copiedCount: newRows.length,
      totalCount: newData.length,
      sourceGridId: dynamicGridOptions.sourceGridId
    });
  }, [selectedRowKeys, value, onChange, config.id, pageId, dynamicGridOptions.sourceGridId]);

  const renderCell = (column: GridColumn, record: GridRow, rowIndex: number) => {
    const cellValue = record[column.id];

    switch (column.type) {
      case 'text':
        return (
          <Input
            value={cellValue || ''}
            placeholder={column.placeholder}
            disabled={column.disabled || config.disabled}
            onChange={(e) => handleCellChange(rowIndex, column.id, e.target.value)}
            style={{ border: 'none', padding: '4px 8px' }}
          />
        );

      case 'dropdown':
        return (
          <Select
            value={cellValue}
            placeholder={column.placeholder}
            disabled={column.disabled || config.disabled}
            onChange={(newValue) => handleCellChange(rowIndex, column.id, newValue)}
            style={{ width: '100%', border: 'none' }}
            size="small"
            allowClear={column.allowClear}
            showSearch={column.searchable}
          >
            {(column.options || []).map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        );

      case 'checkbox':
        return (
          <Checkbox
            checked={cellValue || false}
            disabled={column.disabled || config.disabled}
            onChange={(e) => handleCellChange(rowIndex, column.id, e.target.checked)}
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            value={cellValue || ''}
            placeholder={column.placeholder}
            disabled={column.disabled || config.disabled}
            onChange={(e) => handleCellChange(rowIndex, column.id, e.target.value)}
            style={{ border: 'none', padding: '4px 8px' }}
          />
        );

      case 'readonly':
        return (
          <span style={{ padding: '4px 8px', color: '#666' }}>
            {cellValue || '-'}
          </span>
        );

      default:
        return cellValue || '';
    }
  };

  // Prepare columns for the resizable header hook
  const baseColumns = useMemo(() => [
    ...(dynamicGridOptions.showRowNumbers !== false ? [{
      title: '#',
      key: 'rowNumber',
      width: 50,
      fixed: 'left' as const,
      render: (_: any, __: any, index: number) => index + 1
    }] : []),
    ...generatedColumns.map((column, columnIndex) => {
      // Pin columns based on configuration (default to 1 for row number if present)
      const lockedColumnsCount = dynamicGridOptions.lockedColumns ?? (dynamicGridOptions.showRowNumbers !== false ? 1 : 0);
      const shouldPin = columnIndex < lockedColumnsCount;
      
      return {
        title: column.label,
        key: column.id,
        dataIndex: column.id,
        width: column.width || 120,
        fixed: shouldPin ? 'left' as const : undefined,
        render: (text: any, record: GridRow, index: number) => renderCell(column, record, index),
        resizable: column.resizable !== false,
        minWidth: column.minWidth || 60,
        maxWidth: column.maxWidth
      };
    })
  ], [generatedColumns, dynamicGridOptions.showRowNumbers, dynamicGridOptions.lockedColumns]);

  // Use the resizable header hook
  const { 
    resizableColumns, 
    components: resizableComponents, 
    tableWidth,
    resetColumns 
  } = useAntdResizableHeader({
    columns: baseColumns,
    columnsState: {
      persistenceKey: `dynamic-grid-${config.id}`,
      persistenceType: 'localStorage',
    },
  });

  // Row selection configuration
  const rowSelection = dynamicGridOptions.allowRowSelection !== false ? {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    type: 'checkbox' as const,
    fixed: true,
    columnWidth: 50
  } : undefined;

  // Toolbar
  const toolbar = dynamicGridOptions.showToolbar !== false ? (
    <div style={{ 
      marginBottom: 8, 
      display: 'flex', 
      justifyContent: 'space-between',
      alignItems: 'center'
    }}>
      <Space>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={handleAddRow}
          size="small"
          disabled={config.disabled}
        >
          Add Row
        </Button>
        
        {selectedRowKeys.length > 0 && (
          <>
            <Button 
              icon={<DeleteOutlined />} 
              onClick={handleRemoveRows}
              disabled={config.disabled}
              size="small"
              danger
            >
              Delete
            </Button>
            
            <Button 
              icon={<CopyOutlined />} 
              onClick={handleCopyRows}
              disabled={config.disabled}
              size="small"
            >
              Duplicate
            </Button>
          </>
        )}

        <Tooltip title="Reset column widths to default">
          <Button 
            icon={<ReloadOutlined />} 
            onClick={resetColumns}
            size="small"
            disabled={config.disabled}
          >
            Reset Widths
          </Button>
        </Tooltip>
      </Space>
      
      <span style={{ fontSize: '12px', color: '#666' }}>
        {value.length} row{value.length !== 1 ? 's' : ''}
        {selectedRowKeys.length > 0 && ` (${selectedRowKeys.length} selected)`}
      </span>
    </div>
  ) : null;

  // Error display
  const errorDisplay = error && error.length > 0 ? (
    <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
      {error.join(', ')}
    </div>
  ) : null;

  // Empty state when no source grid data
  if (generatedColumns.length === 0 || generatedColumns.length === (dynamicGridOptions.staticColumns?.length || 0)) {
    return (
      <Form.Item
        label={config.label}
        help={config.description}
        validateStatus={error && error.length > 0 ? 'error' : undefined}
      >
        <div style={{ 
          textAlign: 'center', 
          padding: '40px 20px',
          background: token.colorBgLayout,
          border: '1px dashed #d9d9d9',
          borderRadius: '6px',
          color: '#999'
        }}>
          {dynamicGridOptions.emptyText || 'No data columns configured. Define fields in the source grid first.'}
        </div>
        {errorDisplay}
      </Form.Item>
    );
  }

  const validateRules = () => {
    const rules: any[] = [];
    
    if (config.required) {
      rules.push({
        required: true,
        message: `${config.label} is required`
      });
    }
    
    if (dynamicGridOptions.minRows) {
      rules.push({
        validator: (_: any, value: GridRow[]) => {
          if (value && value.length < dynamicGridOptions.minRows!) {
            return Promise.reject(new Error(`At least ${dynamicGridOptions.minRows} rows are required`));
          }
          return Promise.resolve();
        }
      });
    }
    
    return rules;
  };

  const formItemProps = {
    validateStatus: error && error.length > 0 ? 'error' as const : undefined,
    help: error && error.length > 0 ? error[0] : undefined,
    rules: validateRules()
  };

  return (
    <Form.Item {...formItemProps}>
      <div>
        {/* Grid Title */}
        {config.label && (
          <div style={{ 
            marginBottom: '8px',
            fontWeight: 500,
            fontSize: '14px'
          }}>
            {config.label}
            {config.required && <span style={{ color: '#ff4d4f', marginLeft: '4px' }}>*</span>}
          </div>
        )}
        
        {/* Description */}
        {config.description && (
          <div style={{ 
            marginBottom: '12px',
            fontSize: '12px',
            color: '#666'
          }}>
            {config.description}
          </div>
        )}

        {/* Error Display */}
        {error && error.length > 0 && (
          <div style={{ 
            marginBottom: '12px',
            fontSize: '12px',
            color: '#ff4d4f'
          }}>
            {error[0]}
          </div>
        )}

        {/* Toolbar */}
        {toolbar}

        {/* Grid */}
        <Table
          components={resizableComponents}
          columns={resizableColumns}
          dataSource={value}
          rowSelection={rowSelection}
          pagination={false}
          size="small"
          bordered
          scroll={{ 
            x: tableWidth,
            y: dynamicGridOptions.maxHeight ? parseInt(dynamicGridOptions.maxHeight.replace('px', '')) - 100 : 300
          }}
          style={{
            maxHeight: dynamicGridOptions.maxHeight || '400px'
          }}
          locale={{
            emptyText: dynamicGridOptions.emptyText || 'No data rows configured. Add rows to define the data to be generated.'
          }}
          rowKey="id"
        />

        {/* Footer info */}
        <div style={{ 
          marginTop: 8, 
          fontSize: '12px', 
          color: '#666',
          textAlign: 'right'
        }}>
          {dynamicGridOptions.minRows && `Min: ${dynamicGridOptions.minRows} rows`}
          {dynamicGridOptions.maxRows && ` • Max: ${dynamicGridOptions.maxRows} rows`}
        </div>
      </div>
    </Form.Item>
  );
};
